import type { ReactNode, RefObject } from 'react';
import { useDashboardLimits } from '../limits';
import { isRangePartial, type RangePreset } from '../ranges';
import { useDeveloperProfiles, useSettings } from '../settings';
import { Button } from './Button';
import { RangePicker } from './RangePicker';

const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue';

/**
 * Sticky bar above the page content: page title + data freshness on the
 * left, global filters (time range, developer) and refresh on the right.
 * View navigation lives in the Sidebar, not here.
 */
export function FilterBar({
  title,
  generatedAt,
  freshness,
  isFetching,
  onRefresh,
  preset,
  onPresetChange,
  customRange,
  onCustomRangeChange,
  developers,
  developerId,
  onDeveloperChange,
  onOpenMenu,
  menuButtonRef,
  subtitle,
  rangePresets,
  actions,
}: {
  title: string;
  /** When the page's data was generated. Shown only when `freshness` is not given. */
  generatedAt?: string;
  /** The freshness line (see DataFreshness): latest telemetry in range and query age. */
  freshness?: ReactNode;
  isFetching: boolean;
  onRefresh: () => void;
  preset: RangePreset;
  onPresetChange: (p: RangePreset) => void;
  customRange: { from: string; to: string };
  onCustomRangeChange: (r: { from: string; to: string }) => void;
  /** Omit to hide the developer filter (e.g. on the cross-tool overview). */
  developers?: { developerId: string; events?: number }[];
  developerId?: string;
  onDeveloperChange: (id: string | undefined) => void;
  onOpenMenu: () => void;
  menuButtonRef: RefObject<HTMLButtonElement>;
  /** Replaces the "Telemetry through …" line under the title. */
  subtitle?: ReactNode;
  /** Restrict the time-range presets offered. */
  rangePresets?: RangePreset[];
  /** Extra controls after the refresh button (e.g. the Executive view's download). */
  actions?: ReactNode;
}) {
  const maxLookbackDays = useDashboardLimits().data?.maxLookbackDays;
  const partial = isRangePartial(preset, customRange, maxLookbackDays);
  return (
    <header className="sticky top-0 z-20 flex min-h-[72px] flex-col justify-center border-b border-border bg-surface/90 backdrop-blur">
      <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3 sm:px-8">
        <button
          ref={menuButtonRef}
          type="button"
          onClick={onOpenMenu}
          aria-label="Open navigation"
          className={`-ml-2 flex h-11 w-11 items-center justify-center rounded-input text-text-muted hover:text-text-default lg:hidden ${focusRing}`}
        >
          <i aria-hidden="true" className="fa-solid fa-bars text-lg" />
        </button>

        <div className="mr-auto min-w-0">
          <h1 className="text-[19px] font-bold text-text-default">{title}</h1>
          <p className="text-xs text-text-faint" aria-live="polite">
            {subtitle ?? freshness ?? (generatedAt ? (
              <>
                Generated <b className="font-medium text-text-muted">{new Date(generatedAt).toLocaleString()}</b>
              </>
            ) : (
              ' '
            ))}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {partial && (
            <span
              role="status"
              title={`This range starts before the ${maxLookbackDays}-day data retention window. Older telemetry has expired, so the figures cover the last ${maxLookbackDays} days only.`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill bg-warn-bg px-3 py-1 text-xs font-medium text-warn-ink"
            >
              <i aria-hidden="true" className="fa-solid fa-triangle-exclamation" />
Limited to the last {maxLookbackDays} days: older data is not retained
            </span>
          )}
          <RangePicker
            value={preset}
            onChange={onPresetChange}
            custom={customRange}
            onCustomChange={onCustomRangeChange}
            presets={rangePresets}
            maxLookbackDays={maxLookbackDays}
          />
          {developers && (
            <DeveloperFilter developers={developers} selected={developerId} onChange={onDeveloperChange} />
          )}
          <Button
            type="button"
            variant="secondary"
            className="!min-h-[44px] !w-[44px] !px-0 !py-0"
            onClick={onRefresh}
            disabled={isFetching}
            aria-label="Refresh dashboard data"
            title="Refresh dashboard data"
          >
            <i aria-hidden="true" className={`fa-solid fa-arrows-rotate text-base ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
          {actions}
        </div>
      </div>
    </header>
  );
}

function DeveloperFilter({
  developers,
  selected,
  onChange,
}: {
  developers: { developerId: string; events?: number }[];
  selected?: string;
  onChange: (id: string | undefined) => void;
}) {
  const isFiltered = selected !== undefined;
  const { label } = useDeveloperProfiles();
  const { data: settings } = useSettings();
  const sorted = [...developers].sort((a, b) => label(a.developerId).localeCompare(label(b.developerId)));
  const teams = [...new Set((settings?.developers ?? [])
    .map((developer) => developer.team)
    .filter((team): team is string => Boolean(team)))].sort((a, b) => a.localeCompare(b));
  const selectedTeam = selected?.startsWith('team:') ? selected.slice(5) : undefined;
  return (
    <div
      className={`flex items-center rounded-input border ${
        isFiltered ? 'border-primary-blue bg-surface-alt' : 'border-border bg-surface'
      }`}
    >
      <i aria-hidden="true" className={`fa-solid fa-user pl-3 text-xs ${isFiltered ? 'text-primary-blue' : 'text-text-faint'}`} />
      <select
        aria-label="Filter by developer or team"
        className={`min-h-[42px] max-w-[16rem] truncate bg-transparent py-1.5 pl-2 pr-2 text-sm text-text-default ${
          isFiltered ? 'font-medium' : ''
        } ${focusRing}`}
        value={selected ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">All developers and teams</option>
        {/* A shared link can name a developer with no usage in the current range — keep it selectable. */}
        {isFiltered && selectedTeam === undefined && !developers.some((d) => d.developerId === selected) && <option value={selected}>{label(selected)}</option>}
        {teams.length > 0 && <optgroup label="Teams">
          {teams.map((team) => <option key={team} value={`team:${team}`}>{team}</option>)}
        </optgroup>}
        <optgroup label="Developers">
        {sorted.map((dev) => (
          <option key={dev.developerId} value={dev.developerId}>
            {dev.events === undefined ? label(dev.developerId) : `${label(dev.developerId)} (${dev.events} events)`}
          </option>
        ))}
        </optgroup>
      </select>
      {isFiltered && (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          aria-label="Clear developer filter"
          title="Clear developer filter"
          className={`flex h-[42px] w-[42px] items-center justify-center border-l border-border text-text-muted hover:text-text-default ${focusRing}`}
        >
          <i aria-hidden="true" className="fa-solid fa-xmark" />
        </button>
      )}
    </div>
  );
}
